import React from 'react'
import { Box, Chip, Typography, IconButton, Tooltip, Button } from '@mui/material'
import { ContentCopy, Link as LinkIcon, LocalShippingOutlined } from '@mui/icons-material'
import useCourierServiceType from '../../../hooks/useCourierServiceType'
import {
  NAVY, SUBTLE, BORDER, WHITE,
  SUCCESS, ERROR, WARNING, PRIMARY_BLUE,
  SUCCESS_BG, ERROR_BG, WARNING_BG, INFO_BG,
} from '../../../styles/foundation/colors'
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography'
import { rtoBand3 } from '../rtoBand'
import { parseDateAndTime } from '../../../helpers/UtilityHelper'
import { getTrackingUrl } from '../../../common/trackingUrl'
import { getRider } from './deliveryAttemptInfo'
import { getClientOrderId } from '../list2/ClientOrderIdText'

function getStatusConfig(status) {
  const label = status?.replaceAll('_', ' ') || ''
  if (['DELIVERED', 'RTO_DELIVERED'].includes(status))
    return { color: SUCCESS, bg: SUCCESS_BG, label }
  if (['FAILED_DELIVERY', 'SHIPMENT_HELD', 'RTO', 'RTO_INTRANSIT', 'CANCELLED_ORDER'].includes(status))
    return { color: ERROR, bg: ERROR_BG, label }
  if (['IN_TRANSIT', 'OUT_FOR_DELIVERY', 'OUT_FOR_PICKUP'].includes(status))
    return { color: WARNING, bg: WARNING_BG, label }
  return { color: PRIMARY_BLUE, bg: INFO_BG, label }
}

const tagChipSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.medium,
  fontSize: SIZE['2xs'],
  borderColor: BORDER,
  color: NAVY,
}

export default function StatusBanner({ orderDetails, onOtpCancellationClick, waVerified }) {
  const { getServiceType } = useCourierServiceType()
  const [linkCopied, setLinkCopied] = React.useState(false)

  if (!orderDetails) return null

  const awb = orderDetails.awb_number || ''
  // Shareable, no-login buyer tracking link — the public proship.in tracking app.
  const copyTrackingLink = () => {
    if (!awb) return
    navigator.clipboard?.writeText(getTrackingUrl(awb))
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 1800)
  }
  const channel = orderDetails.channel_name || '-'
  const clientOrderId = getClientOrderId(orderDetails)
  const status = orderDetails.orderStatus || ''
  const statusCfg = getStatusConfig(status)
  // Shown only while out for delivery; after delivery / a failed attempt it stays on the timeline's OFD scan.
  const ofdRider = status === 'OUT_FOR_DELIVERY' ? getRider(orderDetails) : null
  const paymentMode = orderDetails.payment_mode || ''
  const orderType = orderDetails.order_type || ''
  const courierParent = orderDetails.courierDetail?.parent?.includes('Blended')
    ? orderDetails.actualCourierProviderName
    : orderDetails.courierDetail?.parent || ''
  const serviceType = getServiceType(orderDetails.courierDetail?.prozoId)
  // KCI-impacted flag comes through orderTags (string array) — e.g. "KCI_IMPACTED".
  const kciImpacted = (orderDetails.orderTags || []).some((t) => String(t).toUpperCase().includes('KCI'))
  // custom EDD written by a KCI event — order entity field is `customEdd`; the
  // tagged-orders DTO exposes the same value as `kciImpactedEdd`, so read either.
  const customEdd = orderDetails.customEdd || orderDetails.kciImpactedEdd

  const netra = orderDetails.netra_address
  const netraScore = netra?.score != null && !Number.isNaN(Number(netra.score)) ? Math.round(Number(netra.score)) : null
  const netraBand = netra?.band
  const netraChipCfg = netraBand === 'EXCELLENT' ? { color: SUCCESS, bg: SUCCESS_BG }
    : netraBand === 'GOOD' ? { color: PRIMARY_BLUE, bg: INFO_BG }
    : netraBand === 'FAIR' ? { color: '#B78103', bg: WARNING_BG }
    : netraBand === 'POOR' ? { color: ERROR, bg: ERROR_BG }
    : null

  const netraRto = orderDetails.netra_rto
  const rtoB3 = netraRto?.band ? rtoBand3(netraRto.band) : null
  const rtoChipCfg = rtoB3?.key === 'HIGH' ? { color: ERROR, bg: ERROR_BG }
    : rtoB3?.key === 'MEDIUM' ? { color: '#B78103', bg: WARNING_BG }
    : rtoB3?.key === 'LOW' ? { color: SUCCESS, bg: SUCCESS_BG }
    : rtoB3?.key === 'NEW' ? { color: SUBTLE, bg: '#f5f5f5' }
    : null

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: `1px solid ${BORDER}`,
        bgcolor: WHITE,
        overflow: 'hidden',
        mb: 2,
      }}
    >
      <Box sx={{ height: 4, bgcolor: statusCfg.color }} />

      <Box
        sx={{
          p: { xs: 2, md: 2.5 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* Left — identity */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: WEIGHT.bold,
                fontSize: SIZE.xl,
                color: NAVY,
                letterSpacing: '0.5px',
              }}
            >
              {awb}
            </Typography>
            <Tooltip title='Copy AWB'>
              <IconButton
                size='small'
                onClick={() => navigator.clipboard?.writeText(awb)}
                sx={{ color: SUBTLE }}
              >
                <ContentCopy sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {awb && (
              <Tooltip title={linkCopied ? 'Tracking link copied!' : 'Copy tracking link to share with buyer'}>
                <IconButton size='small' onClick={copyTrackingLink} sx={{ color: linkCopied ? SUCCESS : SUBTLE }}>
                  <LinkIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mb: 0.25 }}>
            Reference Number: <Box component='span' sx={{ color: NAVY, fontWeight: WEIGHT.semibold }}>{orderDetails.reference || '-'}</Box> &middot; {channel}
          </Typography>
          {orderDetails.shippingLabelId && (
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mb: 0.25 }}>
              Shipping Label ID: <Box component='span' sx={{ color: NAVY, fontWeight: WEIGHT.semibold }}>{orderDetails.shippingLabelId}</Box>
            </Typography>
          )}
          {clientOrderId && (
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mb: 0.25 }}>
              Client Order ID: <Box component='span' sx={{ color: NAVY, fontWeight: WEIGHT.semibold }}>{clientOrderId}</Box>
            </Typography>
          )}

          {courierParent && (
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE }}>
              {courierParent}
              {serviceType ? ` \u00B7 ${serviceType}` : ''}
            </Typography>
          )}

          {/* Tags */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {kciImpacted && (
              <Tooltip title='This shipment is impacted by a KCI (Keep Customers Informed) event — a known external disruption affecting delivery.'>
                <Chip
                  label='KCI Impacted'
                  size='small'
                  sx={{ ...tagChipSx, fontWeight: WEIGHT.bold, bgcolor: '#FFF3E0', color: '#E65100', border: '1px solid #E6510055' }}
                />
              </Tooltip>
            )}
            {paymentMode && (
              <Chip
                label={paymentMode}
                size='small'
                sx={{
                  fontFamily: FONT,
                  fontWeight: WEIGHT.semibold,
                  fontSize: SIZE['2xs'],
                  bgcolor: paymentMode === 'COD' ? ERROR_BG : SUCCESS_BG,
                  color: paymentMode === 'COD' ? ERROR : SUCCESS,
                  border: `1px solid ${paymentMode === 'COD' ? ERROR : SUCCESS}40`,
                }}
              />
            )}
            {orderType && (
              <Chip label={orderType} size='small' variant='outlined' sx={tagChipSx} />
            )}
            {customEdd && (
              <Tooltip title='Custom EDD — revised delivery date set for this shipment (e.g. via a KCI event).'>
                <Chip
                  label={`Custom EDD ${parseDateAndTime(customEdd, true)}`}
                  size='small'
                  sx={{ ...tagChipSx, fontWeight: WEIGHT.semibold, bgcolor: '#FFF3E0', color: '#E65100', border: '1px solid #E6510055' }}
                />
              </Tooltip>
            )}
            {orderDetails.isPartialPrepaid && (
              <Chip
                label='Partial Prepaid'
                size='small'
                sx={{ ...tagChipSx, bgcolor: WARNING_BG, color: WARNING, borderColor: `${WARNING}60` }}
              />
            )}
            {orderDetails.rto_awb && (
              <Chip
                label={`RTO: ${orderDetails.rto_awb}`}
                size='small'
                sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], bgcolor: ERROR_BG, color: ERROR }}
              />
            )}
            {orderType === 'REVERSE SHIPMENT' && orderDetails.rvp_reason && (
              <Chip
                label={`RVP: ${orderDetails.rvp_reason}`}
                size='small'
                sx={{ ...tagChipSx, bgcolor: WARNING_BG, color: '#e65100', border: `1px solid #e6510040` }}
              />
            )}
            {orderDetails.channelOrderName && (
              <Chip
                label={orderDetails.channelOrderName}
                size='small'
                variant='outlined'
                sx={{ ...tagChipSx, color: SUBTLE }}
              />
            )}
            {orderDetails.orderSource === 'CHANNEL_ORDER' && (
              <Chip
                label={waVerified !== 'null' ? 'WA Verified' : 'WA Unverified'}
                size='small'
                sx={{
                  fontFamily: FONT,
                  fontSize: SIZE['2xs'],
                  bgcolor: waVerified !== 'null' ? SUCCESS_BG : '#f5f5f5',
                  color: waVerified !== 'null' ? SUCCESS : SUBTLE,
                }}
              />
            )}
            {netraScore != null && netraChipCfg && (
              <Chip
                label={`Addr. Quality: ${netraScore}`}
                size='small'
                sx={{
                  fontFamily: FONT,
                  fontWeight: WEIGHT.semibold,
                  fontSize: SIZE['2xs'],
                  bgcolor: netraChipCfg.bg,
                  color: netraChipCfg.color,
                  border: `1px solid ${netraChipCfg.color}40`,
                }}
              />
            )}
            {rtoB3 && rtoChipCfg && (
              <Chip
                label={`RTO Risk: ${rtoB3.label}`}
                size='small'
                sx={{
                  fontFamily: FONT,
                  fontWeight: WEIGHT.semibold,
                  fontSize: SIZE['2xs'],
                  bgcolor: rtoChipCfg.bg,
                  color: rtoChipCfg.color,
                  border: `1px solid ${rtoChipCfg.color}40`,
                }}
              />
            )}
          </Box>
        </Box>

        {/* Right — status */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Chip
            label={statusCfg.label}
            size='medium'
            sx={{
              fontFamily: FONT,
              fontWeight: WEIGHT.bold,
              fontSize: SIZE.xs,
              bgcolor: statusCfg.bg,
              color: statusCfg.color,
              border: `1px solid ${statusCfg.color}40`,
              px: 1,
            }}
          />
          {ofdRider && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                Rider
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY }}>
                {ofdRider.name || '-'}
                {ofdRider.phone ? ` · ${ofdRider.phone}` : ''}
              </Typography>
            </Box>
          )}
          {awb && (
            <Button
              size='small'
              variant='contained'
              startIcon={<LocalShippingOutlined sx={{ fontSize: 16 }} />}
              onClick={() => window.open(getTrackingUrl(awb), '_blank', 'noopener,noreferrer')}
              sx={{
                textTransform: 'none', borderRadius: '10px', fontFamily: FONT,
                fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, boxShadow: 'none',
              }}
            >
              Track your shipment
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {orderDetails.otpVerifiedCancellation && (
              <Chip
                label='OTP Cancelled'
                size='small'
                onClick={onOtpCancellationClick}
                sx={{
                  fontFamily: FONT,
                  fontSize: SIZE['2xs'],
                  bgcolor: ERROR_BG,
                  color: ERROR,
                  cursor: 'pointer',
                  '&:hover': { filter: 'brightness(0.95)' },
                }}
              />
            )}
            {(orderDetails.otpVerifiedDelivery || orderDetails.deliveryOtpSent) && (
              <Chip
                label='OTP Delivery'
                size='small'
                sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], bgcolor: SUCCESS_BG, color: SUCCESS }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
